// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import { Form, FormInstance } from 'antd';
import { convertToLocaleId, useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CaseCustomField, CustomStatusMapType, UserOutput } from 'thehive-sdk';
import { Select } from 'antd';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { onOrganisationImageUpload } from '@Helpers/download';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { IconLabel } from '@Atoms/Info';
import { CFValueItem } from '@Molecules/CFList/CFValueItem';
import { ScopeContext } from '@Context/ScopeProvider';

export interface AlertChangeStatusValuesForm {
    status: string;
    summary?: string;
    assignee?: string;
    customFields?: Array<CaseCustomField>;
}

interface AlertChangeStatusFormProps {
    onFinish(values: AlertChangeStatusValuesForm): void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    statusList: CustomStatusMapType;
    onCancel: () => void;
    assignableUsers?: UserOutput[];
    initialValues?: {
        status?: string;
        summary?: string;
        customFields?: Array<CaseCustomField>;
        assignee?: string | null;
    };
    useCustomField?: boolean;
}
const { OptGroup } = Select;

export const AlertChangeStatusForm = ({
    onFinish,
    form,
    onChange,
    isValid,
    statusList,
    initialValues,
    assignableUsers,
    onCancel,
    useCustomField = false,
}: AlertChangeStatusFormProps): ReactElement => {
    const i = useLocalMessage();
    const { customFieldList } = useContext(ScopeContext);
    const caseCustomFields = Form.useWatch<CaseCustomField[]>(['customFields'], form);

    const mandatoryCustomFields = useMemo(
        () =>
            (initialValues?.customFields || [])?.filter(({ value, name }) =>
                customFieldList?.find(
                    ({ mandatory, name: cfName }) =>
                        cfName === name && mandatory && (value === undefined || value === null),
                ),
            ),
        [initialValues?.customFields, customFieldList],
    );

    return (
        <THForm<AlertChangeStatusValuesForm>
            name="alert-change-status-form"
            onFinish={onFinish}
            form={form}
            initialValues={initialValues}
            onValuesChange={onChange}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i(`general.confirm`)} />}
            >
                <THForm.Item
                    label={i('alert.change-status.form.status.label')}
                    name="status"
                    rules={[{ required: true, message: i('alert.change-status.form.status.required') }]}
                >
                    <THSelect>
                        {Object.entries(statusList).map(([key, values]) => (
                            <OptGroup key={key} label={key}>
                                {(values || []).map(({ value, colour }) => (
                                    <THSelect.Option key={value} value={value}>
                                        <CustomColorLabel
                                            value={i({
                                                id: `alert.data.status.${convertToLocaleId(value)}`,
                                                defaultMessage: value,
                                            })}
                                            color={colour}
                                        />
                                    </THSelect.Option>
                                ))}
                            </OptGroup>
                        ))}
                    </THSelect>
                </THForm.Item>

                <THForm.Item label={i('alert.change-status.form.summary.label')} name="summary">
                    <THMarkdown sameSize onImageUpload={onOrganisationImageUpload} />
                </THForm.Item>

                <THForm.Item label={i('alert.change-status.form.assignee.label')} name="assignee">
                    <THSelect allowClear borderless placeholder={i('alert.change-status.form.assignee.placeholder')}>
                        {assignableUsers?.map(({ _id, name, avatar, login }) => (
                            <THSelect.Option key={_id} value={login}>
                                <IconLabel key={_id} icon={<UserAvatar name={name} avatar={avatar} />} label={name} />
                            </THSelect.Option>
                        ))}
                    </THSelect>
                </THForm.Item>

                {useCustomField && (
                    <THForm.List name="customFields">
                        {(fields) => {
                            return caseCustomFields?.map((customField, index) => (
                                <CFValueItem
                                    key={customField?._id}
                                    customField={customField}
                                    fieldName={fields[index]?.name}
                                    borderless={false}
                                    hidden={!mandatoryCustomFields.find(({ _id }) => customField._id === _id)}
                                    mandatory={!!mandatoryCustomFields.find(({ _id }) => customField._id === _id)}
                                />
                            ));
                        }}
                    </THForm.List>
                )}
            </THForm.Layout>
        </THForm>
    );
};
